import { useEffect, useMemo, useState } from 'react';
import type { InventorizationContext } from '@/features/inventorization/domain/inventorization-context';
import type { InventorizationIdentity } from '@/features/inventorization/domain/inventorization-identity';
import type { InventorizationPosition } from '@/features/inventorization/domain/inventorization-position';
import type {
  InventorizationMainReading,
  InventorizationRegisterReading,
} from '@/features/inventorization/domain/inventorization-reading';
import { DEFAULT_INVENTORIZATION_REGISTER_ID } from '@/features/inventorization/domain/inventorization-registers';
import type { InventorizationTechnicalDetails } from '@/features/inventorization/domain/inventorization-technical';
import { INVENTORIZE_REVIEW_STATUSES } from '@/features/inventorization/domain/review/inventorization-review-status';
import { createResolvedInventorizationValueMap } from '@/features/inventorization/domain/review/inventorization-value-resolver';
import {
  createInventorizeTechnicalDetailReviewFieldId,
  INVENTORIZE_IDENTITY_REVIEW_FIELD_IDS,
  INVENTORIZE_POSITION_REVIEW_FIELD_IDS,
  INVENTORIZE_REVIEW_FIELD_ID_PREFIXES,
  INVENTORIZE_TECHNICAL_REVIEW_FIELD_IDS,
  readInventorizeRegisterReviewFieldId,
} from '@/features/inventorization/domain/review/inventorize-review-field-ids';
import type { InventorizeReviewField } from '@/features/inventorization/domain/review/inventorize-review-fields';
import { createFieldWizardDefinition } from '@/features/inventorization/fieldWizardRegistry';
import { useLocalPhotoBlob } from '@/features/inventorization/photos/useReviewPhotos';
import { getRegisterPhotos } from '@/features/inventorization/review/register-review-model';
import { useReviewFieldOptions } from '@/features/inventorization/useReviewFieldOptions';
import { AccessibilityFieldWizard } from '@/routers/inventorization/components/AccessibilityFieldWizard';
import { BuiltInOutputFieldWizard } from '@/routers/inventorization/components/BuiltInOutputFieldWizard';
import { GenericFieldEditor } from '@/routers/inventorization/components/GenericFieldEditor';
import { PhotoCategoryFieldWizard } from '@/routers/inventorization/components/photos/PhotoCategoryFieldWizard';
import { FloorplanPositionFieldWizard } from '@/routers/inventorization/components/position/FloorplanPositionFieldWizard';
import { GpsPositionPicker } from '@/routers/inventorization/components/position/GpsPositionPicker';
import { ReviewStatusTag } from '@/routers/inventorization/components/ReviewStatusTag';
import type { CapturedPhotoAsset } from '@/shared/camera/photo-capture-adapter';
import type { CaptureSession, LocalPhotoRef } from '@/shared/capture/capture-session';
import type { PhotoSlotDefinition } from '@/shared/capture/inventorization-photo-slots';
import { translateInventoryText } from '@/shared/i18n/inventory-i18n';

interface InventorizeFieldWizardProps {
  context: InventorizationContext;
  field: InventorizeReviewField;
  identity: InventorizationIdentity;
  isCapturingSlotId: string | null;
  onBack: () => void;
  onCapturePhoto: (
    slot: PhotoSlotDefinition,
    asset: CapturedPhotoAsset,
    replacedPhoto?: LocalPhotoRef,
  ) => Promise<unknown>;
  onDeletePhoto: (photo: LocalPhotoRef) => Promise<unknown>;
  onRefresh: () => Promise<unknown> | unknown;
  onSaveContext: (patch: Partial<InventorizationContext>) => Promise<unknown> | unknown;
  onSaveIdentity: (patch: Partial<InventorizationIdentity>) => Promise<unknown> | unknown;
  onSavePosition: (patch: Partial<InventorizationPosition>) => Promise<unknown> | unknown;
  onSaveReading: (patch: Partial<InventorizationMainReading>) => Promise<unknown> | unknown;
  onSaveRegisterReading: (
    registerId: string,
    patch: Partial<InventorizationRegisterReading>,
  ) => Promise<unknown> | unknown;
  onSaveTechnical: (patch: Partial<InventorizationTechnicalDetails>) => Promise<unknown> | unknown;
  position: InventorizationPosition;
  reading: InventorizationMainReading;
  registers: InventorizationRegisterReading[];
  session: CaptureSession;
  technical: InventorizationTechnicalDetails;
}

export function InventorizeFieldWizard({
  context,
  field,
  identity,
  isCapturingSlotId,
  onBack,
  onCapturePhoto,
  onDeletePhoto,
  onRefresh,
  onSaveContext,
  onSaveIdentity,
  onSavePosition,
  onSaveReading,
  onSaveRegisterReading,
  onSaveTechnical,
  position,
  reading,
  registers,
  session,
  technical,
}: InventorizeFieldWizardProps) {
  const resolvedValues = useMemo(() => createResolvedInventorizationValueMap(session), [session]);
  const effectiveManufacturer = useMemo(() => {
    if (identity.manufacturer) {
      return identity.manufacturer;
    }

    return resolvedValues.get(INVENTORIZE_IDENTITY_REVIEW_FIELD_IDS.manufacturer)?.value ?? '';
  }, [identity.manufacturer, resolvedValues]);
  const ownerType = useMemo(
    () =>
      technical.detail.ownerType ??
      resolvedValues.get(createInventorizeTechnicalDetailReviewFieldId('ownerType'))?.value ??
      '',
    [resolvedValues, technical.detail.ownerType],
  );
  const fieldOptions = useReviewFieldOptions(effectiveManufacturer, identity.commodity);
  const definition = useMemo(
    () =>
      createFieldWizardDefinition({
        buildingOptions: fieldOptions.buildingOptions,
        context,
        field,
        manufacturerOptions: fieldOptions.manufacturerOptions,
        identity,
        meterModels: fieldOptions.meterModels,
        modelOptions: fieldOptions.modelOptions,
        onSaveContext,
        onSaveIdentity,
        onSavePosition,
        onSaveReading,
        onSaveRegisterReading,
        onSaveTechnical,
        ownerType,
        ownerSuggestions: fieldOptions.ownerSuggestions,
        position,
        reading,
        registers,
        technical,
        tenantOptions: fieldOptions.tenantOptions,
        utilityCompanyOwnerOptions: fieldOptions.utilityCompanyOwnerOptions,
      }),
    [
      fieldOptions.buildingOptions,
      fieldOptions.manufacturerOptions,
      fieldOptions.meterModels,
      fieldOptions.modelOptions,
      fieldOptions.ownerSuggestions,
      fieldOptions.tenantOptions,
      fieldOptions.utilityCompanyOwnerOptions,
      context,
      field,
      identity,
      onSaveContext,
      onSaveIdentity,
      onSavePosition,
      onSaveReading,
      onSaveRegisterReading,
      onSaveTechnical,
      ownerType,
      position,
      reading,
      registers,
      technical,
    ],
  );
  const initialValue =
    field.status === INVENTORIZE_REVIEW_STATUSES.parsed || field.status === INVENTORIZE_REVIEW_STATUSES.unsure
      ? field.value
      : definition.currentValue;
  const [value, setValue] = useState(initialValue);
  const sourcePhoto = useLocalPhotoBlob(readRegisterSourcePhoto(session, field, registers));

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  if (field.id.startsWith(INVENTORIZE_REVIEW_FIELD_ID_PREFIXES.photos)) {
    return (
      <PhotoCategoryFieldWizard
        field={field}
        isCapturingSlotId={isCapturingSlotId}
        onBack={onBack}
        onCapturePhoto={onCapturePhoto}
        onDeletePhoto={onDeletePhoto}
        onRefresh={onRefresh}
        session={session}
      />
    );
  }

  if (field.id === INVENTORIZE_POSITION_REVIEW_FIELD_IDS.gps) {
    return (
      <GpsPositionPicker
        backLabel={translateInventoryText('app.inventory.position.actions.backToReview', 'Back to review')}
        description={translateInventoryText(
          'app.inventory.position.gps.description',
          'Use current GPS, tap the map to place this meter, or drag the marker to refine it. Grey pins are other cached meters in this location.',
        )}
        excludeMeterId={session._id}
        headerExtra={<ReviewStatusTag status={field.status} />}
        onBack={onBack}
        onSave={patch => {
          onSavePosition(patch);
          onBack();
        }}
        position={position}
        saveLabel={translateInventoryText('app.inventory.position.actions.savePin', 'Save pin')}
        section={field.section}
        title={field.label}
      />
    );
  }

  if (field.id === INVENTORIZE_POSITION_REVIEW_FIELD_IDS.floorplan) {
    return (
      <FloorplanPositionFieldWizard
        field={field}
        onBack={onBack}
        onSavePosition={onSavePosition}
        position={position}
        session={session}
      />
    );
  }

  if (field.id === INVENTORIZE_TECHNICAL_REVIEW_FIELD_IDS.accessibility) {
    return (
      <AccessibilityFieldWizard field={field} onBack={onBack} onSaveTechnical={onSaveTechnical} technical={technical} />
    );
  }

  if (field.id.startsWith(INVENTORIZE_REVIEW_FIELD_ID_PREFIXES.technicalOutputs)) {
    return (
      <BuiltInOutputFieldWizard field={field} onBack={onBack} onSaveTechnical={onSaveTechnical} technical={technical} />
    );
  }

  return (
    <GenericFieldEditor
      definition={definition}
      field={field}
      onBack={onBack}
      setValue={setValue}
      sourcePhoto={sourcePhoto}
      value={value}
    />
  );
}

function readRegisterSourcePhoto(
  session: CaptureSession,
  field: InventorizeReviewField,
  registers: InventorizationRegisterReading[],
) {
  const registerId =
    readInventorizeRegisterReviewFieldId(field.id)?.registerId ??
    (field.id.startsWith('reading.') ? DEFAULT_INVENTORIZATION_REGISTER_ID : undefined);

  if (!registerId) {
    return undefined;
  }

  const register = registers.find(candidate => candidate.id === registerId) ?? { id: registerId, photoRef: registerId };
  return getRegisterPhotos(
    session.photos.filter(photo => !photo.uploadedRemoteId),
    register,
  ).at(-1);
}
